'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import styles from './MeshFlow.module.css';

/* Client-side only: it mounts a WebGL context, and next/dynamic keeps the
   shader runtime (~77KB gzipped) out of the server bundle and off the initial
   page chunk. */
const MeshGradient = dynamic(
  () => import('@paper-design/shaders-react').then((m) => m.MeshGradient),
  { ssr: false },
);

/* The studies hero SVG's own stops, with the deep two repeated so four of the
   five mesh spots sit at the dark end. The SVG's flat fill #4361A1 is
   deliberately absent: it sat mid-ladder and closed the gap either side of it,
   and the spots merged into one colour. */
const HUB_COLORS = ['#6DA5DC', '#1F387F', '#0B1435', '#1F387F', '#0B1435'];

const SPEED = 0.6;
const SCALE = 1.35;
const DISTORTION = 0.8;
const SWIRL = 0.1;

/* Mount the shader once the page is idle, and not at all under reduced motion
   or data-saver. The layer underneath (the hero SVG on /science/studies, the
   dark card on /science/explainers) is the first paint either way, so LCP
   never waits on the shader chunk. */
const useShaderReady = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const { connection } = navigator as Navigator & {
      connection?: { saveData?: boolean };
    };
    if (connection?.saveData) return undefined;
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => setReady(true), {
        timeout: 2000,
      });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(() => setReady(true), 1);
    return () => clearTimeout(timer);
  }, []);
  return ready;
};

const MeshFlow = ({ colors = HUB_COLORS }: { colors?: string[] }) => {
  const ready = useShaderReady();
  return (
    <div className={styles.wrap} aria-hidden="true">
      {ready ? (
        <MeshGradient
          colors={colors}
          speed={SPEED}
          scale={SCALE}
          distortion={DISTORTION}
          swirl={SWIRL}
          width="100%"
          height="100%"
        />
      ) : null}
    </div>
  );
};

export default MeshFlow;
